import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { workspaceApi } from '@/lib/api'
import { Plus, Lock, Globe, Users } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import type { WorkspaceVisibility } from '@/types/workspace'

const VISIBILITY_LABEL: Record<WorkspaceVisibility, string> = {
  private: 'Privat',
  public: 'Publik',
  invite_only: 'Invite Only',
}

const VISIBILITY_ICON: Record<WorkspaceVisibility, typeof Lock> = {
  private: Lock,
  public: Globe,
  invite_only: Users,
}

export default function WorkspacesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspaceApi.getWorkspaces,
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Workspace</h1>
          <p className="text-sm text-muted-foreground">Kelola semua workspace kamu.</p>
        </div>
        <Link
          to="/workspaces/new"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" />
          Buat Workspace
        </Link>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">Gagal memuat workspace. Coba refresh halaman.</p>
      )}

      {!isLoading && !error && data && data.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-16 text-center">
          <p className="text-sm text-muted-foreground">Belum ada workspace.</p>
          <Link
            to="/workspaces/new"
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <Plus className="h-4 w-4" />
            Buat workspace pertama kamu
          </Link>
        </div>
      )}

      {!isLoading && !error && data && data.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((ws) => {
            const VisibilityIcon = VISIBILITY_ICON[ws.visibility]
            return (
              <Link
                key={ws.id}
                to={`/workspaces/${ws.slug}`}
                className="group rounded-xl border border-border bg-card p-5 transition-colors hover:bg-secondary"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="truncate font-semibold tracking-tight text-card-foreground group-hover:text-primary">
                      {ws.name}
                    </h2>
                    <p className="text-xs text-muted-foreground">/{ws.slug}</p>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                    <VisibilityIcon className="h-3 w-3" />
                    {VISIBILITY_LABEL[ws.visibility]}
                  </span>
                </div>
                {ws.description && (
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                    {ws.description}
                  </p>
                )}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
